<template>
  <div data-testid="kiosk-create-slp-form">
    <Loader :is-processing="state.isLoading" />
    <div class="kiosk-pass"></div>
    <div class="mb-2">
      <Label
        class="d-block text-start mb-1"
        required
        html-for="kiosk-slp-destination"
        label-id="kiosk-slp-destination-lbl"
      >
        {{ $t("kiosk.destination") }}
      </Label>
      <LazyLoadSelect
        data-test-id="kiosk-slp-pass-destination"
        input-id="kiosk-slp-destination"
        labelled-by="kiosk-slp-destination-lbl"
        hide-floating-label
        :accessible-label="$t('kiosk.destination')"
        :context="
          activeKiosk && activeKiosk.school ? activeKiosk.school.context : null
        "
        :invalid-feedback="errors.to ? $t(errors.to) : ''"
        :is-multiple="false"
        :selected="state.form.to"
        type="kiosk"
        :adult-pass-request-disabled="true"
        :is-in-kiosk="true"
        :is-s-l-p="true"
        :required="true"
        :refresh-on-open="true"
        :placeholder="$t('common.select_one')"
        @changed="checkUnavailable"
      />
    </div>

    <InfoBox v-if="destinationUnavailability" class="mb-2 danger">
      {{
        destinationUnavailability.unavailable_type == "App\\Models\\Room"
          ? $t("kiosk.this_room_is_unavailable_until")
          : $t("kiosk.this_adult_is_unavailable_until")
      }}
      <span
        class="mx-1"
        v-html="
          $helpers.getHTMLDateTime(
            destinationUnavailability.to_date,
            'MMMM D, YYYY',
            'h:mm A'
          ) + '.'
        "
      />
      <span v-if="destinationUnavailability.comment"
        >{{ $t("kiosk.note") }} {{ destinationUnavailability.comment }}</span
      >
    </InfoBox>

    <div
      v-if="favorites && favorites.length"
      class="kiosk-favorites-text text-start"
    >
      {{ $t("kiosk.or_quick_select_from") }}
      <span class="kiosk-favorites-text-bolder">{{
        $t("kiosk.favorites")
      }}</span>
    </div>

    <FavoriteCarousel
      v-if="favorites && favorites.length"
      :teacher="true"
      :key="'slp-destination'"
      :is-editable="false"
      :not-managable="true"
      :action="
        (elem) => {
          checkUnavailable(elem)
          getFavorites()
        }
      "
      class="mb-3"
      section="destination"
      :selected="state.form.to"
      :options="favorites"
      :is-global="true"
    />

    <div
      v-if="
        state.form &&
        state.form.to &&
        state.form.to.value &&
        state.form.to.value.comment_type != 'Hidden'
      "
      class="mb-3"
    >
      <Label html-for="kiosk-slp-reason" label-id="kiosk-slp-reason-lbl">
        {{ $t("kiosk.reason") }}
      </Label>
      <InputField
        id="kiosk-slp-reason"
        data-test-id="kiosk-slp-pass-comment"
        v-model="state.form.reason"
        :invalid-feedback="errors.reason ? $t(errors.reason) : ''"
        max-chars="250"
        :rows="4"
        class="w-full"
        type="textarea"
        :placeholder="
          state.form.to.value.comment_type == 'Optional'
            ? $t('kiosk.reason_for_pass_optional')
            : $t('kiosk.reason_for_pass_required')
        "
      />
    </div>

    <div class="tardy-late-pass d-flex flex-column mb-3">
      <p class="mb-1 text-start">
        {{ $t("kiosk.mark_this_pass_as") }}
      </p>
      <TardyRadioGroup
        v-model="state.form.tardy_badge"
        :is-allowed-lates="true"
        :allowed-lates="isTardyCreateAllow"
        :auto-select-single-allowed-option="false"
        :disabled="isTardyGroupDisabled"
      />
      <div
        v-if="state.isTardyRequired"
        class="invalid-feedback d-block"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        {{ $t("common.this_option_is_required") }}
      </div>
    </div>

    <div class="d-flex justify-content-center gap-3">
      <BaseButton
        class="w-50 d-flex align-items-center justify-content-center"
        rounded
        @click="cancel()"
      >
        {{ $t("kiosk.cancel") }}
      </BaseButton>
      <BaseButton
        data-test-id="create-kiosk-slp-form-submit"
        class="w-50 d-flex align-items-center justify-content-center"
        rounded
        solid
        @click="createSlpPass()"
      >
        {{ $t("kiosk.submit") }}
      </BaseButton>
    </div>

    <div v-if="state.reqResponse.message">
      <InfoBox
        class="my-4"
        :class="{ danger: state.reqResponse.type === 'danger' }"
        :announce-assertive="!!state.reqResponse.message"
        :message="state.reqResponse.message"
      />
    </div>
  </div>
</template>

<script>
import FavoriteCarousel from "@/v3components/FavoriteCarousel.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import TardyRadioGroup from "@/v3components/Forms/TardyRadioGroup.vue"
import { useStore } from "vuex"
import { computed, reactive } from "vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import { useI18n } from "vue-i18n"

export default {
  name: "CreateKioskSlpPassForm",
  components: {
    FavoriteCarousel,
    LazyLoadSelect,
    Loader,
    Label,
    InputField,
    BaseButton,
    TardyRadioGroup,
    InfoBox
  },
  emits: ["tabChanged", "kioskSlpCreated"],
  setup(props, { emit }) {
    const store = useStore()
    const { t } = useI18n()

    const state = reactive({
      isLoading: false,
      favoritesCollection: null,
      form: {
        to: [],
        reason: "",
        tardy_badge: ""
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      isTardyRequired: false
    })

    const activeKiosk = computed(() => store.getters["kiosks/activeKiosk"])
    const access_token = computed(() => store.getters["kiosks/accessToken"])

    const isTardyCreateAllow = computed(() => {
      return (
        activeKiosk.value?.allowed_late_passes || { ltc: false, lts: false }
      )
    })

    const isTardyGroupDisabled = computed(() => {
      const { lts, ltc } = isTardyCreateAllow.value
      return !(lts || ltc)
    })

    const favorites = computed(() => {
      return state.favoritesCollection || activeKiosk.value?.favorites
    })

    const destinationUnavailability = computed(() => {
      return state.form.to && state.form.to.value
        ? state.form.to.value.unavailability
        : null
    })

    const validationMessages = {
      required: "kiosk.this_field_is_required"
    }

    const validations = {
      form: {
        to: {
          required: helpers.withMessage(validationMessages.required, required),
          available: (value) => {
            if (value) {
              return !destinationUnavailability.value
            }
            return true
          }
        },
        reason: {
          required: helpers.withMessage(
            validationMessages.required,
            requiredIf(() => {
              if (state.form.to && state.form.to.value) {
                return state.form.to.value.comment_type == "Optional"
                  ? false
                  : state.form.to.value.comment_type == "Hidden"
                    ? false
                    : true
              }
              return false
            })
          )
        }
      }
    }

    const v$ = useVuelidate(validations.form, state.form, {
      $stopPropagation: true
    })

    const isValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })
      return errorObj
    })

    const createSlpPass = () => {
      resetResponseMessages()
      state.isTardyRequired = false

      if (!isValid.value || !state.form.tardy_badge) {
        v$.value.$touch()
        if (!state.form.tardy_badge) {
          state.isTardyRequired = true
        }
        return
      }

      if (!access_token.value) {
        emit("tabChanged", "userLogin")
        return
      }

      const pass = {
        to_id: state.form.to.value.id,
        to_type: state.form.to.value.type,
        comment: state.form.reason,
        kiosk_id: activeKiosk.value.id,
        kiosk_passcode: activeKiosk.value.passcode,
        tardy_badge: state.form.tardy_badge
      }

      state.isLoading = true
      store
        .dispatch("kiosks/createKioskSlpPass", pass)
        .then((response) => {
          state.isLoading = false
          emit("kioskSlpCreated", buildSlpPassSnapshot(response))
          resetForm()
        })
        .catch((err) => {
          const response = err?.response?.data || {}
          setErrorResponse(response.message, response.errors)
          state.isLoading = false
        })
    }

    const buildSlpPassSnapshot = (response) => {
      const payload = response?.data || {}
      const pass = payload.pass || payload.data || payload
      const destination = state.form.to?.value || {}
      const destinationName =
        destination.name ||
        state.form.to?.label ||
        `${destination.first_name || ""} ${destination.last_name || ""}`.trim()

      return {
        ...pass,
        type: pass?.type || "SLP",
        tardy_badge: pass?.tardy_badge || state.form.tardy_badge,
        created_at: pass?.created_at || new Date().toISOString(),
        from: pass?.from || { name: destinationName },
        to: pass?.to || { name: destinationName }
      }
    }

    const getFavorites = () => {
      const context = activeKiosk.value?.school?.context
      if (!context) return

      store.dispatch("kiosks/getFavorites", context).then((response) => {
        if (response?.data?.data?.length) {
          state.favoritesCollection = response.data.data
        }
      })
    }

    const setErrorResponse = (message, responseErrors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : t("kiosk.something_went_wrong"),
        errors: responseErrors ? responseErrors : {}
      }
    }

    const resetResponseMessages = () => {
      state.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    }

    const resetForm = () => {
      state.form = {
        to: [],
        reason: "",
        tardy_badge: ""
      }
      state.isTardyRequired = false
      v$.value.$reset()
    }

    const cancel = () => {
      emit("tabChanged", "userLogin")
      store.dispatch("kiosks/userLogout")
    }

    const checkUnavailable = (value) => {
      state.form.to = value
      if (!value?.value?.id || !value?.value?.type) return

      store
        .dispatch("kiosks/getUnavailability", {
          id: value.value.id,
          type: value.value.type
        })
        .then((response) => {
          if (response?.data?.data) {
            state.form.to.value.unavailability = response.data.data
          }
        })
    }

    return {
      state,
      activeKiosk,
      isTardyCreateAllow,
      isTardyGroupDisabled,
      favorites,
      destinationUnavailability,
      createSlpPass,
      cancel,
      getFavorites,
      errors,
      checkUnavailable
    }
  }
}
</script>
